import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Loader2, Star, TrendingUp, Gem, Eye, ShieldCheck, Mail } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { sellerService } from '../../../services/sellerService';
import { manufacturingService } from '../../../services/manufacturingService';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';

export default function TierUpgradeModal({ currentTier, accountId, onClose, onUpgradeSuccess, isManufacturer = false, shopType = null, isSignUp = false }) {
  const { user, refreshUserProfile } = useAuth(); // Destructure refreshUserProfile to sync local roles!
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(currentTier);

  // New Credit Card States for Onboarding
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');

  // Native success screen confirmation instead of popups
  const [isSuccessScreen, setIsSuccessScreen] = useState(false);
  const [successTier, setSuccessTier] = useState('');

  // Enterprise inquiry loader states
  const [submittingInquiry, setSubmittingInquiry] = useState(false);

  // Dynamic Plans State from Database settings
  const [dbPlans, setDbPlans] = useState(null);

  useEffect(() => {
    const fetchDbPlans = async () => {
      try {
        const { data, error } = await supabase
          .from('platform_settings')
          .select('value')
          .eq('key', 'subscription_plans')
          .maybeSingle();
        if (!error && data && data.value) {
          setDbPlans(data.value);
        }
      } catch (err) {
        console.error('Failed to load dynamic subscription plans:', err);
      }
    };
    fetchDbPlans();
  }, []);

  const getTierDetails = (tierId, categoryKey, fallbackService) => {
    if (dbPlans && dbPlans[categoryKey] && dbPlans[categoryKey][tierId]) {
      return dbPlans[categoryKey][tierId];
    }
    return fallbackService.getTierInfo(tierId);
  };

  const rawTiers = isManufacturer ? [
    { id: 'manufacturers_view_only', ...getTierDetails('manufacturers_view_only', 'manufacturer', manufacturingService), icon: Eye, colorClass: 'text-slate-500 bg-slate-100', borderColor: 'border-slate-200 hover:border-slate-400' },
    { id: 'workshop', ...getTierDetails('workshop', 'manufacturer', manufacturingService), icon: Star, colorClass: 'text-gray-500 bg-gray-100', borderColor: 'border-gray-200 hover:border-gray-400' },
    { id: 'factory', ...getTierDetails('factory', 'manufacturer', manufacturingService), icon: TrendingUp, colorClass: 'text-indigo-600 bg-indigo-100', borderColor: 'border-indigo-200 hover:border-indigo-400' },
    { id: 'foundry', ...getTierDetails('foundry', 'manufacturer', manufacturingService), icon: Gem, colorClass: 'text-yellow-600 bg-yellow-100', borderColor: 'border-yellow-200 hover:border-yellow-400' }
  ] : shopType === 'repair' ? [
    { id: 'repair_view_only', ...getTierDetails('repair_view_only', 'repair', sellerService), icon: Eye, colorClass: 'text-slate-500 bg-slate-100', borderColor: 'border-slate-200 hover:border-slate-400' },
    { id: 'repair_entry', ...getTierDetails('repair_entry', 'repair', sellerService), icon: Star, colorClass: 'text-gray-500 bg-gray-100', borderColor: 'border-gray-200 hover:border-gray-400' },
    { id: 'repair_mid', ...getTierDetails('repair_mid', 'repair', sellerService), icon: TrendingUp, colorClass: 'text-indigo-600 bg-indigo-100', borderColor: 'border-indigo-200 hover:border-indigo-400' },
    { id: 'repair_premium', ...getTierDetails('repair_premium', 'repair', sellerService), icon: Gem, colorClass: 'text-yellow-600 bg-yellow-100', borderColor: 'border-yellow-200 hover:border-yellow-400' }
  ] : shopType === 'wrap' ? [
    { id: 'wrap_view_only', ...getTierDetails('wrap_view_only', 'wrap', sellerService), icon: Eye, colorClass: 'text-slate-500 bg-slate-100', borderColor: 'border-slate-200 hover:border-slate-400' },
    { id: 'wrap_entry', ...getTierDetails('wrap_entry', 'wrap', sellerService), icon: Star, colorClass: 'text-gray-500 bg-gray-100', borderColor: 'border-gray-200 hover:border-gray-400' },
    { id: 'wrap_mid', ...getTierDetails('wrap_mid', 'wrap', sellerService), icon: TrendingUp, colorClass: 'text-indigo-600 bg-indigo-100', borderColor: 'border-indigo-200 hover:border-indigo-400' },
    { id: 'wrap_premium', ...getTierDetails('wrap_premium', 'wrap', sellerService), icon: Gem, colorClass: 'text-yellow-600 bg-yellow-100', borderColor: 'border-yellow-200 hover:border-yellow-400' }
  ] : [
    { id: 'seller_view_only', ...getTierDetails('seller_view_only', 'seller', sellerService), icon: Eye, colorClass: 'text-slate-500 bg-slate-100', borderColor: 'border-slate-200 hover:border-slate-400' },
    { id: 'entry', ...getTierDetails('entry', 'seller', sellerService), icon: Star, colorClass: 'text-gray-500 bg-gray-100', borderColor: 'border-gray-200 hover:border-gray-400' },
    { id: 'mid_tier', ...getTierDetails('mid_tier', 'seller', sellerService), icon: TrendingUp, colorClass: 'text-indigo-600 bg-indigo-100', borderColor: 'border-indigo-200 hover:border-indigo-400' },
    { id: 'platinum', ...getTierDetails('platinum', 'seller', sellerService), icon: Gem, colorClass: 'text-yellow-600 bg-yellow-100', borderColor: 'border-yellow-200 hover:border-yellow-400' }
  ];

  // 1. Hide the $0 plan when signing up, only after a signup should that option be shown!
  let tiers = rawTiers;
  if (isSignUp) {
    tiers = tiers.filter(t => !t.id.includes('view_only'));
  }

  const handleEnterpriseInquiry = async () => {
    if (!accountId) {
      toast.error('Unable to verify active seller credentials.');
      return;
    }
    
    setLoading(true);
    try {
      // 1. Fetch exact business name and contact email for the seller_account
      const { data: sellerAcc } = await supabase
        .from('seller_accounts')
        .select('id, business_name, business_email, phone')
        .eq('id', accountId)
        .maybeSingle();
        
      const businessName = sellerAcc?.business_name || 'Active Partner';
      const contactEmail = sellerAcc?.business_email || user?.email || 'N/A';
      const contactPhone = sellerAcc?.phone || 'N/A';
      
      // 2. Insert secure inquiry row into our newly created enterprise_inquiries database table!
      const { error } = await supabase
        .from('enterprise_inquiries')
        .insert({
          seller_id: accountId,
          business_name: businessName,
          contact_email: contactEmail,
          contact_phone: contactPhone,
          notes: `B2B custom pricing request submitted for high-tech ${shopType === 'repair' ? 'Repair Shop' : shopType === 'wrap' ? 'Wrap Shop' : 'Seller'} tier.`
        });
        
      if (error) throw error;
      
      setSuccessTier('enterprise');
      setIsSuccessScreen(true);
    } catch (e) {
      console.error('Failed to submit enterprise inquiry:', e);
      toast.error('Submission failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!isSignUp && selectedTier === currentTier) {
      onClose();
      return;
    }

    // New Secure Credit Card Validation for Onboarding
    if (isSignUp && selectedTier !== 'enterprise') {
      if (!cardName || !cardNumber || !cardExpiry || !cardCvc) {
        toast.error('Please fill out all payment credentials before continuing.');
        return;
      }
    }

    setLoading(true);
    try {
      if (selectedTier === 'enterprise') {
        await handleEnterpriseInquiry();
        return;
      }

      if (isManufacturer) {
        await manufacturingService.changeTier(accountId, selectedTier);
      } else {
        await sellerService.changeTier(accountId, selectedTier);
      }
      
      // Refresh local user profile and roles to prevent 403 unauthorized locks!
      if (refreshUserProfile) {
        await refreshUserProfile();
      }
      
      setSuccessTier(selectedTier);
      setIsSuccessScreen(true);
    } catch (err) {
      console.error('Failed to change tier:', err);
      toast.error('Failed to update subscription tier. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isSuccessScreen) {
    const activeRole = shopType === 'repair' 
      ? 'Repair Shop' 
      : shopType === 'wrap' 
        ? 'Wrap Shop' 
        : 'Seller Account';
    const planName = successTier === 'enterprise' ? 'Enterprise' : successTier.split('_').pop().toUpperCase();
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <div 
          className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl p-8 text-center animate-in scale-in duration-200 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
          onClick={e => e.stopPropagation()}
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600 animate-bounce">
            <CheckCircle2 className="w-12 h-12" />
          </div>

          <h2 className="text-2xl font-black uppercase tracking-wide text-gray-900 dark:text-white mb-2">
            {successTier === 'enterprise' ? 'Inquiry Request Submitted!' : 'Shop Plan Activated!'}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-300 leading-relaxed mb-6 font-semibold">
            {successTier === 'enterprise' ? (
              <span>Your activation request for <span className="text-indigo-600 font-black">{activeRole} (Enterprise)</span> has been securely registered! Our Sales Team will get back to you within 24 hours.</span>
            ) : (
              <span>Congratulations! Your <span className="text-indigo-600 font-black">{activeRole} ({planName})</span> subscription has been successfully activated. Your billing credentials have been securely verified and are billed annually.</span>
            )}
          </p>

          <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-4 text-left space-y-2.5 mb-8">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400 font-bold">Authorized Account:</span>
              <span className="font-extrabold text-slate-800 dark:text-white">{user?.email}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400 font-bold">Activation Status:</span>
              <span className="text-green-600 font-extrabold flex items-center gap-1">★ Active</span>
            </div>
          </div>

          <button
            type="button"
            onClick={async () => {
              if (onUpgradeSuccess) {
                await onUpgradeSuccess(successTier);
              }
              onClose();
            }}
            className="w-full h-12 bg-indigo-600 hover:bg-indigo-500 text-white font-black uppercase text-xs tracking-wider rounded-xl shadow-lg shadow-indigo-600/10 transition-colors cursor-pointer"
          >
            Go to Shop Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-card w-full max-w-6xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-border text-left">
          <div>
            <h2 className="text-2xl font-bold">
              {isSignUp 
                ? `Activate Your ${shopType === 'repair' ? 'Repair Shop' : 'Wrap Shop'} Plan` 
                : shopType === 'repair'
                  ? 'Upgrade Your Repair Shop Plan'
                  : shopType === 'wrap'
                    ? 'Upgrade Your Wrap Shop Plan'
                    : 'Upgrade Your Seller Account Plan'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-left">
            {tiers.map(tier => {
              const Icon = tier.icon;
              const isSelected = selectedTier === tier.id;
              const isCurrent = currentTier === tier.id;

              return (
                <div 
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  className={`relative flex flex-col rounded-xl border-2 p-6 cursor-pointer transition-all ${
                    isSelected ? 'border-primary ring-1 ring-primary/20 shadow-md' : tier.borderColor
                  } ${isCurrent ? 'opacity-80 bg-muted/30 ring-2 ring-emerald-500' : 'bg-card'}`}
                >
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm shadow-emerald-600/10">
                      Current Plan
                    </div>
                  )}

                  {((shopType === 'repair' && tier.id === 'repair_mid') || (shopType === 'wrap' && tier.id === 'wrap_mid')) && (
                    <div className="absolute -top-3 right-4 bg-indigo-600 text-white text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-sm shadow-indigo-600/10">
                      Most Popular
                    </div>
                  )}

                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${tier.colorClass}`}>
                    <Icon className="w-6 h-6" />
                  </div>

                  <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                  <div className="flex flex-col mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-black">
                        {typeof tier.price === 'number' ? `$${tier.price}` : tier.price}
                      </span>
                      {typeof tier.price === 'number' && (
                        <span className="text-muted-foreground text-sm font-medium">/month</span>
                      )}
                    </div>
                    {typeof tier.price === 'number' && (
                      <span className="text-[10px] text-indigo-500 font-extrabold uppercase mt-0.5">billed annually</span>
                    )}
                  </div>

                  <ul className="space-y-3 flex-1 mb-6">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className={`w-full py-2.5 rounded-lg text-center font-semibold text-sm transition-colors ${
                    isSelected 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}>
                    {isSelected ? 'Selected' : 'Select Plan'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Secure B2B Enterprise Custom Pricing Inquiry Bottom Banner */}
          {!isManufacturer && (
            <div className="mt-8 bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col md:flex-row items-center justify-between gap-4 text-white text-left shadow-lg relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-purple-500/5 to-indigo-500/5 pointer-events-none" />
              <div className="flex items-center gap-3.5 z-10">
                <div className="w-12 h-12 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-xl flex items-center justify-center shrink-0">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <h4 className="text-sm font-black uppercase tracking-wider text-purple-300">Enterprise Network Plans</h4>
                  <p className="text-xs text-slate-300 leading-normal max-w-xl mt-0.5">
                    Are you a large-scale B2B fleet operator, multi-location technician network, or custom wrap franchise? Complete our custom pricing request, and our sales team will reach out within 24 hours.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleEnterpriseInquiry}
                className="bg-purple-600 hover:bg-purple-500 text-white font-black uppercase text-xs h-10 px-6 rounded-xl shrink-0 transition-all cursor-pointer shadow-md shadow-purple-600/10 border-none z-10"
              >
                Inquire Now
              </button>
            </div>
          )}

          {/* Secure Credit Card Payment Method Section for Onboarding */}
          {isSignUp && selectedTier !== 'enterprise' && (
            <div className="mt-8 border-t border-border pt-8 max-w-2xl mx-auto animate-in fade-in duration-200 text-left">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-indigo-600 animate-pulse" />
                    Secure Payment Credentials
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5 font-bold">Please provide your credit card details to complete your B2B subscription setup.</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400 font-bold bg-gray-50 border border-gray-200 px-3 py-1 rounded-full shadow-sm">
                  <Eye size={13} className="text-indigo-500" /> Secure SSL Connection
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-black uppercase text-slate-500 mb-1.5 tracking-wide">Cardholder Name *</label>
                  <input 
                    type="text" 
                    value={cardName} 
                    onChange={(e) => setCardName(e.target.value)} 
                    placeholder="e.g. Johnathan Miller"
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 outline-none font-medium text-gray-900"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase text-slate-500 mb-1.5 tracking-wide">Card Number *</label>
                  <input 
                    type="text" 
                    value={cardNumber} 
                    onChange={(e) => {
                      const v = e.target.value.replace(/\s?/g, '').replace(/[^0-9]/g, '');
                      const matches = v.match(/\d{4,16}/g);
                      const match = (matches && matches[0]) || '';
                      const parts = [];
                      for (let i = 0, len = match.length; i < len; i += 4) {
                        parts.push(match.substring(i, i + 4));
                      }
                      setCardNumber(parts.length > 0 ? parts.join(' ') : e.target.value);
                    }} 
                    maxLength="19"
                    placeholder="4000 1234 5678 9010"
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 outline-none font-medium text-gray-900"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-black uppercase text-slate-500 mb-1.5 tracking-wide">Expiry *</label>
                    <input 
                      type="text" 
                      value={cardExpiry} 
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        setCardExpiry(v.length >= 2 ? v.substring(0, 2) + '/' + v.substring(2, 4) : e.target.value);
                      }} 
                      maxLength="5"
                      placeholder="MM/YY"
                      className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 outline-none font-medium text-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase text-slate-500 mb-1.5 tracking-wide">CVC *</label>
                    <input 
                      type="password" 
                      value={cardCvc} 
                      onChange={(e) => setCardCvc(e.target.value.replace(/[^0-9]/g, ''))} 
                      maxLength="4"
                      placeholder="•••"
                      className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 outline-none font-medium text-gray-900"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex justify-end gap-3 bg-muted/10 rounded-b-2xl items-center">
          <Button variant="outline" onClick={onClose} disabled={loading} className="h-11 px-6">
            Cancel
          </Button>
          <Button 
            onClick={handleUpgrade} 
            disabled={loading || (!isSignUp && selectedTier === currentTier)}
            className="h-11 px-6 min-w-[200px]"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            ) : isSignUp ? (
              `Activate Your ${shopType === 'repair' ? 'Repair Shop' : 'Wrap Shop'} Plan`
            ) : (
              'Confirm Selection'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}